import { v4 as uuidv4 } from 'uuid';

/** 本地存储的 key 前缀 */
const DRILLDOWN_PREFIX = 'statsDetailParams-';
const DOWNLOAD_PREFIX = 'downLoadParams-';

/** 默认过期时间（毫秒），24 小时 */
const DEFAULT_EXPIRE_MS = 24 * 60 * 60 * 1000;

type TLocalStoreItem<T = any> = {
  id: string;
  data: T;
  /** 存储时间戳 */
  storedAt: number;
  /** 过期时间戳（绝对值） */
  expiresAt: number;
};

/**
 * 写入存储（带过期时间）
 * @param storage 存储介质（sessionStorage / localStorage）
 */
function set<T = any>(
  storage: Storage,
  prefix: string,
  data: T,
  expireMs = DEFAULT_EXPIRE_MS,
): string {
  const id = uuidv4();
  const now = Date.now();
  const item: TLocalStoreItem<T> = {
    id,
    data,
    storedAt: now,
    expiresAt: now + expireMs,
  };
  storage.setItem(`${prefix}${id}`, JSON.stringify(item));
  return id;
}

/**
 * 读取存储（自动判断过期）
 * @param storage 存储介质（sessionStorage / localStorage）
 */
function get<T = any>(storage: Storage, prefix: string, id: string): T | null {
  try {
    const raw = storage.getItem(`${prefix}${id}`);
    if (!raw) return null;

    const item: any = JSON.parse(raw);

    // 新版格式：{ id, data, storedAt, expiresAt } 包裹结构，返回 data 并做过期检查
    if (
      item &&
      typeof item === 'object' &&
      typeof item.data !== 'undefined' &&
      typeof item.storedAt === 'number' &&
      typeof item.expiresAt === 'number'
    ) {
      if (Date.now() > item.expiresAt) {
        storage.removeItem(`${prefix}${id}`);
        return null;
      }
      return item.data as T;
    }

    // 旧版格式：直接存储参数对象（未包裹），原样返回
    return item as T;
  } catch {
    return null;
  }
}

/**
 * 删除指定 key
 */
function remove(storage: Storage, prefix: string, id: string): void {
  storage.removeItem(`${prefix}${id}`);
}

/**
 * 清理指定前缀下所有过期数据
 * @param storage 存储介质（sessionStorage / localStorage）
 */
function cleanExpired(storage: Storage, prefix: string): void {
  const now = Date.now();
  const keysToRemove: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key?.startsWith(prefix)) {
      try {
        const raw = storage.getItem(key);
        if (raw) {
          const item: TLocalStoreItem = JSON.parse(raw);
          if (now > item.expiresAt) {
            keysToRemove.push(key);
          }
        }
      } catch {
        keysToRemove.push(key);
      }
    }
  }
  keysToRemove.forEach((key) => storage.removeItem(key));
}

// ============ 下钻参数（sessionStorage）============

/**
 * 写入下钻参数到 sessionStorage
 */
export const setDrilldownLocal = (data: any, expireMs?: number): string => {
  return set(sessionStorage, DRILLDOWN_PREFIX, data, expireMs);
};

/**
 * 从 sessionStorage 读取下钻参数
 */
export const getDrilldownLocal = (id: string): any | null => {
  return get(sessionStorage, DRILLDOWN_PREFIX, id);
};

/**
 * 删除下钻参数
 */
export const removeDrilldownLocal = (id: string): void => {
  remove(sessionStorage, DRILLDOWN_PREFIX, id);
};

/**
 * 清理所有过期的下钻参数
 */
export const cleanExpiredDrilldownLocal = (): void => {
  cleanExpired(sessionStorage, DRILLDOWN_PREFIX);
};

// ============ 下载参数（localStorage）============

/**
 * 写入下载参数到 localStorage
 */
export const setDownloadLocal = (data: any, expireMs?: number): string => {
  return set(localStorage, DOWNLOAD_PREFIX, data, expireMs);
};

/**
 * 从 localStorage 读取下载参数
 */
export const getDownloadLocal = (id: string): any | null => {
  return get(localStorage, DOWNLOAD_PREFIX, id);
};

/**
 * 删除下载参数
 */
export const removeDownloadLocal = (id: string): void => {
  remove(localStorage, DOWNLOAD_PREFIX, id);
};

/**
 * 清理所有过期的下载参数
 */
export const cleanExpiredDownloadLocal = (): void => {
  cleanExpired(localStorage, DOWNLOAD_PREFIX);
};

// ============ 一次性清理 ============

/**
 * 一次性清理所有过期数据（建议在页面初始化时调用）
 */
export const cleanAllExpiredLocal = (): void => {
  cleanExpiredDrilldownLocal();
  cleanExpiredDownloadLocal();
};
